import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { useSelector, useDispatch } from 'react-redux';
import mapboxgl from 'mapbox-gl';

import PropertyPopup from './components/PropertyPopup';

import { updateIsSearching, updateHoveredPropertyId, updateModalPropertyId } from '../../../../../../../../reducers/_Pages/Public/radarSlice';

const Radar = () => {
  const { user } = useSelector((state) => state.user);
  const { map, properties } = useSelector((state) => state.radar);
  const dispatch = useDispatch();
  const markersRef = useRef({});
  const popupsRef = useRef({});
  const closeTimeoutRef = useRef(null);
  const currentOpenPopupRef = useRef(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeAllPopups = () => {
    if (currentOpenPopupRef.current) {
      currentOpenPopupRef.current.remove();
      currentOpenPopupRef.current = null;
    }
    
    Object.values(popupsRef.current).forEach(({ popup }) => {
      if (popup.isOpen()) {
        popup.remove();
      }
    });
  };

  const closePopupWithTimeout = (popup, timeout) => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      popup.remove();
      if (currentOpenPopupRef.current === popup) {
        currentOpenPopupRef.current = null;
      }
      dispatch(updateHoveredPropertyId(null));
    }, timeout);
  };

  useEffect(() => {
    if (!map) return;
    
    let isMounted = true;
    
    const isTouchDevice = ('ontouchstart' in window) || 
                          (navigator.maxTouchPoints > 0) || 
                          (navigator.msMaxTouchPoints > 0);
    
    if (properties.length < 1) {
      Object.values(markersRef.current).forEach(({ marker }) => {
        marker.remove();
      });
      markersRef.current = {};
      popupsRef.current = {};
      currentOpenPopupRef.current = null;
      return;
    }

    Object.values(markersRef.current).forEach(({ marker }) => {
      marker.remove();
    });
    markersRef.current = {};
    popupsRef.current = {};
    currentOpenPopupRef.current = null;

    const shuffledProperties = shuffleArray(properties);

    const addMarkerWithDelay = (index) => {
      if (!isMounted) return;
      
      if (index >= shuffledProperties.length) {
        setTimeout(() => {
          if (isMounted) dispatch(updateIsSearching(false));
        }, 1000);
        
        Object.values(markersRef.current).forEach(({ el }, i) => {
          el.classList.add('z-10');
        });
        return;
      }

      const { id, lng, lat, area } = shuffledProperties[index];

      const el = document.createElement('div');
      const pin = document.createElement('div');
      const areaLabel = document.createElement('div');

      el.className = 'before:content-[\'\'] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-14 before:h-14 before:bg-ih-blue/40 before:rounded-full before:scale-0 before:transition-transform before:duration-200 before:pointer-events-none before:-z-10';
      pin.className = `bg-ih-blue ${user.id ? 'w-3 h-3' : 'w-4 h-4'} rounded-full cursor-pointer relative z-10`;
      areaLabel.className = 'text-white fixed -top-1 left-1/2 -translate-x-1/2 -translate-y-full px-2 py-1 bg-ih-indigo rounded-3xl whitespace-nowrap';
      areaLabel.textContent = `${Math.round(area)} m²`;
      el.appendChild(pin);

      if (user.id) pin.appendChild(areaLabel);

      const popupNode = document.createElement('div');
      const root = createRoot(popupNode);
      root.render(<PropertyPopup property={shuffledProperties[index]} />);

      const popup = new mapboxgl.Popup({
        offset: 5,
        closeButton: false,
        closeOnClick: false,
        maxWidth: 'none',
        className: 'property-popup-custom'
      }).setDOMContent(popupNode);

      popupsRef.current[id] = { popup, root };

      const activateCircle = () => {
        el.classList.add('before:scale-100');
        el.classList.remove('before:scale-0');
      };

      const deactivateCircle = () => {
        el.classList.add('before:scale-0');
        el.classList.remove('before:scale-100');
      };

      // Hover effect uniquement sur desktop
      if (!isTouchDevice) {
        el.addEventListener('mouseenter', () => {
          clearCloseTimeout();
          closeAllPopups();
          
          activateCircle();
          
          dispatch(updateHoveredPropertyId(id));
          popup.setLngLat([lng, lat]).addTo(map.current);
          currentOpenPopupRef.current = popup;
          
          const popupElements = document.querySelectorAll('.property-popup-custom');
          const popupElement = popupElements[popupElements.length - 1];
          if (popupElement) {
            popupElement.addEventListener('mouseenter', () => {
              clearCloseTimeout();
              activateCircle();
            });
            popupElement.addEventListener('mouseleave', () => {
              deactivateCircle();
              closePopupWithTimeout(popup, 200);
            });
          }
        });

        el.addEventListener('mouseleave', () => {
          setTimeout(() => {
            const popupElements = document.querySelectorAll('.property-popup-custom');
            const isHoveringPopup = Array.from(popupElements).some(p => p.matches(':hover'));
            if (!isHoveringPopup) {
              deactivateCircle();
            }
          }, 10);
          
          closePopupWithTimeout(popup, 200);
        });
      }

      pin.addEventListener('click', () => {
        dispatch(updateModalPropertyId(id));
      });

      const marker = new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .addTo(map.current);

      markersRef.current[id] = { marker, el };

      const delay = Math.floor(Math.random() * 50);
      setTimeout(() => addMarkerWithDelay(index + 1), delay);
    };

    addMarkerWithDelay(0);

    return () => {
      isMounted = false;
      
      clearCloseTimeout();
      Object.values(popupsRef.current).forEach(({ popup, root }) => {
        popup.remove();
        root.unmount();
      });
      currentOpenPopupRef.current = null;
    };
  }, [map, properties, user.id]);



  return null;
};

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default Radar;
